import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../utils/apiClient';
import BrandLogo from '../components/BrandLogo';

/**
 * "Sign Up Free" lands here (app host, /signup).
 *
 * This is a sign-up REQUEST, not self-provisioning. Operator accounts are
 * created by platform staff (2026-07-25: two tenants once signed themselves
 * up as facility owners, so the self-serve endpoint became a 410). The
 * prospect says who they are, the brand, and how many locations; they get
 * a welcome email right away, the platform inbox gets the alert, and the
 * portfolio + first location are provisioned for them to test.
 */
const inputCls =
  'w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all';

const CONTACT_EMAIL = 'info@mysms360.com';

const TURNSTILE_SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const TURNSTILE_LOAD_ERROR = 'The verification could not load. Reload the page to try again.';

// Loads Cloudflare's widget script once and resolves with window.turnstile.
// Resolves at once when it is already present (a re-mount, or a test).
const loadTurnstile = () => new Promise((resolve, reject) => {
  if (window.turnstile) { resolve(window.turnstile); return; }
  let script = document.querySelector(`script[src="${TURNSTILE_SCRIPT}"]`);
  if (!script) {
    script = document.createElement('script');
    script.src = TURNSTILE_SCRIPT;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }
  script.addEventListener('load', () => resolve(window.turnstile));
  script.addEventListener('error', () => reject(new Error('turnstile script failed to load')));
});

const Signup = () => {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', brand_name: '', location_count: '1', message: '',
  });
  const [status, setStatus] = useState('idle'); // idle | sending | done
  const [error, setError] = useState('');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Bot check. The widget appears only when the site key is configured and
  // the server enforces it only when its secret is, so the two can be
  // switched on independently. Each token is single-use: after a refused
  // submission the widget is reset before a retry.
  const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';
  const [captcha, setCaptcha] = useState('');
  const [captchaError, setCaptchaError] = useState('');
  const widgetRef = useRef(null);
  const widgetId = useRef(null);
  useEffect(() => {
    if (!siteKey || status === 'done') return undefined;
    let cancelled = false;
    loadTurnstile().then((ts) => {
      if (cancelled || !widgetRef.current || widgetId.current !== null) return;
      widgetId.current = ts.render(widgetRef.current, {
        sitekey: siteKey,
        action: 'signup',
        callback: (token) => setCaptcha(token),
        'expired-callback': () => setCaptcha(''),
        'error-callback': () => { setCaptcha(''); setCaptchaError(TURNSTILE_LOAD_ERROR); },
      });
    }).catch(() => setCaptchaError(TURNSTILE_LOAD_ERROR));
    return () => { cancelled = true; };
  }, [siteKey, status]);

  const resetCaptcha = () => {
    if (siteKey && window.turnstile && widgetId.current !== null) {
      window.turnstile.reset(widgetId.current);
    }
    setCaptcha('');
  };

  const submit = async (e) => {
    e.preventDefault();
    // Honeypot is uncontrolled and read BEFORE the await (the event is
    // released after the synchronous phase).
    const honeypot = e.currentTarget.elements.namedItem('website')?.value || '';
    if (siteKey && !captcha) {
      setError('Please complete the verification first.');
      return;
    }
    setError('');
    setStatus('sending');
    const params = new URLSearchParams(window.location.search);
    const utm = {};
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach((k) => {
      if (params.get(k)) utm[k] = params.get(k);
    });
    try {
      await apiClient.post('/api/website/saas/signup', {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim() || null,
        brand_name: form.brand_name.trim(),
        location_count: String(form.location_count).trim(),
        message: form.message.trim() || null,
        website: honeypot,
        referrer: document.referrer || undefined,
        received_at: new Date().toISOString(),
        ...utm,
        ...(siteKey ? { turnstile_token: captcha } : {}),
      });
      setStatus('done');
    } catch (err) {
      setStatus('idle');
      resetCaptcha();
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 422) {
        setError('Please check the email address and try again.');
      } else if (typeof d === 'string') {
        setError(d);
      } else {
        setError(`Something went wrong. Email us at ${CONTACT_EMAIL} and we will set you up by hand.`);
      }
    }
  };

  const label = (text, extra) => (
    <label className="block text-[13px] font-medium text-slate-700 mb-1.5">
      {text}{extra && <span className="text-slate-400 font-normal"> {extra}</span>}
    </label>
  );

  return (
    <div
      className="min-h-screen flex items-center justify-center py-10"
      style={{ backgroundColor: 'var(--c-bg)' }}
    >
      <div className="max-w-sm w-full">
        <div className="text-center mb-8">
          <BrandLogo className="mb-4" />
          <p className="text-[13px] text-slate-400 mt-1">
            {status === 'done'
              ? 'You are signed up'
              : 'Free. We set up your portfolio and first location for you.'}
          </p>
        </div>

        <div className="rounded-xl border border-slate-200/80 bg-white p-6">
          {status === 'done' ? (
            <div className="space-y-3">
              <h2 className="text-[15px] font-semibold text-slate-900">Check your inbox.</h2>
              <p className="text-[13px] text-slate-600 leading-relaxed">
                A welcome email is on its way. Within one business day you will get your
                sign-in, with your brand and first location already set up so you can test
                with real workflows.
              </p>
              <p className="text-[12px] text-slate-400">
                Questions before then? Reply to the welcome email or write to {CONTACT_EMAIL}.
              </p>
              <Link to="/login" className="block text-[13px] text-blue-600 hover:text-blue-700 font-medium">
                Already have an account? Sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              {error && (
                <div className="rounded-lg bg-red-50 border border-red-200/80 px-3 py-2.5 text-[13px] text-red-600">{error}</div>
              )}
              {/* Honeypot: invisible to people, bots fill it; the server
                  stores nothing when it has a value. */}
              <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
              <div>
                {label('Your name')}
                <input type="text" autoComplete="name" required value={form.name}
                       onChange={set('name')} className={inputCls} placeholder="Jordan Lee"
                       disabled={status === 'sending'} />
              </div>
              <div>
                {label('Work email')}
                <input type="email" autoComplete="email" required value={form.email}
                       onChange={set('email')} className={inputCls} placeholder="you@yourstorage.com"
                       disabled={status === 'sending'} />
              </div>
              <div>
                {label('Phone', '(optional)')}
                <input type="tel" autoComplete="tel" value={form.phone}
                       onChange={set('phone')} className={inputCls} placeholder="(555) 555-0100"
                       disabled={status === 'sending'} />
              </div>
              <div>
                {label('Brand or company name')}
                <input type="text" autoComplete="organization" required value={form.brand_name}
                       onChange={set('brand_name')} className={inputCls} placeholder="Northgate Self Storage"
                       disabled={status === 'sending'} />
              </div>
              <div>
                {label('Number of locations')}
                <input type="number" min="1" step="1" inputMode="numeric" required
                       value={form.location_count} onChange={set('location_count')}
                       className={inputCls} disabled={status === 'sending'} />
              </div>
              <div>
                {label('Anything else', '(optional)')}
                <textarea rows={3} value={form.message} onChange={set('message')}
                          className={inputCls}
                          placeholder="What you use today, what is not working, what you want to try first"
                          disabled={status === 'sending'} />
              </div>
              {siteKey && (
                <div>
                  <div ref={widgetRef} data-testid="turnstile-widget" />
                  {captchaError && (
                    <p className="text-[12px] text-red-600 mt-1">{captchaError}</p>
                  )}
                </div>
              )}
              <button
                type="submit"
                disabled={status === 'sending' || (Boolean(siteKey) && !captcha)}
                className="w-full py-2 px-4 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {status === 'sending' ? 'Sending…' : 'Sign up free'}
              </button>
              <p className="text-[12px] text-slate-400 text-center">
                Already have an account?{' '}
                <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">Sign in</Link>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Signup;
